"use client"

import CommonTitle from "@/ui/common/bit/CommonTitle"
import BlockWrapper from "@/ui/common/BlockWrapper"
import ContactBox from "@/ui/common/ContactBox"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useInView } from "react-intersection-observer"
import { mediaQueries, useInViewConfig, useTransitionUpStyles } from "ui"
import { colors, sizes } from "ui/theme/theme"
import { extractBlockProperties } from "utils/simplifyData"

const StyledBlockWrapper = styled(BlockWrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`
const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${sizes.baseTriple};

  ${mediaQueries.desktopM} {
    gap: ${sizes.baseQuadric};
  }
`
const StyledCardsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: fit-content;
  gap: ${sizes.baseDouble};

  ${mediaQueries.desktopM} {
    flex-direction: row;
    gap: ${sizes.baseTriple};
  }
`
const StyledTextCardsContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: ${sizes.baseDouble};

  ${mediaQueries.desktopM} {
    gap: ${sizes.baseTriple};
  }
`
const StyledAllCardsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: ${sizes.baseDouble};

  ${mediaQueries.desktopM} {
    flex-direction: row;
    width: auto;
    gap: ${sizes.baseTriple};
  }
`

const StyledTextItemBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseSingle};
  position: relative;
  padding: ${sizes.baseSingle};
  * {
    text-align: start;
    color: ${colors.black};
    & h5 {
      text-align: start;
      color: ${colors.red};
    }
  }
  &:not(:first-child)::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: -${sizes.baseDouble};
    right: 0;
    height: 100%;
    width: 1px;
    background-color: ${colors.borderGray};
  }
`

const StyledTypography = styled(Typography)`
  color: ${colors.black};

  ${mediaQueries.mobile} {
    text-align: center;
  }
`

interface TextItem {
  title?: string
  textItem?:
    | {
        title?: string
        text?: string
      }
    | {
        title?: string
        text?: string
      }[]
}

interface CardItem {
  text?: string
  button?: { href: string; text: string }
  primaryColor?: string
  icon?: string
}

interface CardsWithExternalLinksTextProps {
  data: {
    title?: { value: string }
    subtitle?: { value: string }
    text?: { value: string }
    textItems?: { blocks: any[] }
    cardItems?: { blocks: any[] }
  }
  id?: string
}

const CardsWithExternalLinksText = ({
  data,
  id,
}: CardsWithExternalLinksTextProps) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)
  const { title, subtitle, text, textItems, cardItems } = data
  const parsedTextItems = extractBlockProperties(
    textItems?.blocks || [],
  ) as TextItem[]
  const parsedCardItems = extractBlockProperties(
    cardItems?.blocks || [],
  ) as CardItem[]

  return (
    <StyledBlockWrapper id={id}>
      <StyledContainer ref={ref} style={transitionStyles}>
        {title?.value && (
          <CommonTitle title={title.value} subtitle={subtitle?.value} />
        )}
        <StyledAllCardsContainer>
          {parsedCardItems?.length > 0 && (
            <StyledCardsContainer>
              {parsedCardItems.map((item, index) => {
                const { text, icon, button, primaryColor } = item
                return (
                  <ContactBox
                    key={`card-item-${index}`}
                    data={{
                      title: text || "",
                      primaryColor: primaryColor || "00A7B5",
                      icon: icon,
                      isInView: inView,
                      url: button
                        ? {
                            url: button?.href || "",
                            name: button.text || "",
                          }
                        : undefined,
                    }}
                  />
                )
              })}
            </StyledCardsContainer>
          )}
          {parsedTextItems?.length > 0 && (
            <StyledTextCardsContainer>
              {parsedTextItems.map((item, index) => {
                const { title: itemTitle, textItem } = item
                return (
                  <StyledTextItemBox key={`text-item-${index}`}>
                    {itemTitle && (
                      <Typography variant="h4" component="h3">
                        {itemTitle}
                      </Typography>
                    )}
                    {Array.isArray(textItem) &&
                      textItem.map((text, textIndex) => {
                        const { title, text: textContent } = text
                        return (
                          <Box key={`text-item-${index}-content-${textIndex}`}>
                            {title && (
                              <Typography variant="h5" component={"p"}>
                                {title}
                              </Typography>
                            )}
                            {textContent && (
                              <Typography variant="body2">
                                {textContent}
                              </Typography>
                            )}
                          </Box>
                        )
                      })}
                  </StyledTextItemBox>
                )
              })}
            </StyledTextCardsContainer>
          )}
        </StyledAllCardsContainer>
        {text?.value && (
          <StyledTypography variant="body2">{text.value}</StyledTypography>
        )}
      </StyledContainer>
    </StyledBlockWrapper>
  )
}

export default CardsWithExternalLinksText
